import { Link, useLocation, useParams } from "@tanstack/react-router"; // Import useParams
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import type { NavCollapsible, NavGroupType, NavItem, NavLink } from "./types";

function useOptionalTenantId(): string | undefined {
  const params = useParams({ strict: false }) as { tenantId?: string };
  return params?.tenantId;
}

const TENANT_PLACEHOLDER = "__no_tenant__";

export function NavGroup({ title, items }: NavGroupType) {
  const tenantId = useOptionalTenantId();
  const { state } = useSidebar();
  const href = useLocation({ select: (location) => location.href });
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{title}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          const key =
            "items" in item
              ? `${item.title}-group`
              : `${item.title}-${item.url}`;

          if ("items" in item) {
            const collapsible = item as NavCollapsible;
            if (state === "collapsed") {
              return (
                <SidebarMenuCollapsedDropdown
                  href={href}
                  item={collapsible}
                  key={key}
                  tenantId={tenantId}
                />
              );
            }
            return (
              <SidebarMenuCollapsible
                href={href}
                item={collapsible}
                key={key}
                tenantId={tenantId}
              />
            );
          }

          const link = item as NavLink;
          return (
            <SidebarMenuLink
              href={href}
              item={link}
              key={key}
              tenantId={tenantId}
            />
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

const NavBadge = ({ children }: { children: ReactNode }) => (
  <Badge className="rounded-full px-1 py-0 text-xs">{children}</Badge>
);

const SidebarMenuLink = ({
  item,
  href,
  tenantId,
}: {
  item: NavLink;
  href: string;
  tenantId: string | undefined;
}) => {
  const { setOpenMobile } = useSidebar();
  const resolvedUrl = resolveNavUrl(item.url, tenantId);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={checkIsActive(href, item, tenantId)}
        tooltip={item.title}
      >
        <Link onClick={() => setOpenMobile(false)} to={resolvedUrl}>
          {item.icon && <item.icon />}
          <span>{item.title}</span>
          {item.badge && <NavBadge>{item.badge}</NavBadge>}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const SidebarMenuCollapsible = ({
  item,
  href,
  tenantId,
}: {
  item: NavCollapsible;
  href: string;
  tenantId: string | undefined;
}) => {
  return (
    <Collapsible
      asChild
      className="group/collapsible" // Pass potentially undefined tenantId
      defaultOpen={checkIsActive(href, item, tenantId, true)}
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={item.title}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="CollapsibleContent">
          <SidebarMenuSub>
            {item.items.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                {/* Render sub-item using SidebarMenuLink logic */}
                {/* Pass tenantId down to the link component */}
                <SidebarMenuLink
                  href={href}
                  item={subItem}
                  tenantId={tenantId}
                />
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};
// Define helper component *before* it's used in SidebarMenuCollapsedDropdown
const SidebarMenuLinkWrapper = ({
  item,
  href,
  tenantId,
}: {
  item: NavLink;
  href: string;
  tenantId: string | undefined;
}) => {
  const resolvedUrl = resolveNavUrl(item.url, tenantId);

  return (
    <Link
      className={checkIsActive(href, item, tenantId) ? "bg-secondary" : ""}
      to={resolvedUrl}
    >
      {item.icon && <item.icon />}
      <span className="max-w-52 text-wrap">{item.title}</span>
      {item.badge && <span className="ml-auto text-xs">{item.badge}</span>}
    </Link>
  );
};

const SidebarMenuCollapsedDropdown = ({
  item,
  href,
  tenantId,
}: {
  item: NavCollapsible;
  href: string;
  tenantId: string | undefined;
}) => {
  // No need to get params here directly, sub-items handle it via SidebarMenuLink
  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            isActive={checkIsActive(href, item, tenantId, true)}
            tooltip={item.title} // Check sub-items for parent active state
          >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="right" sideOffset={4}>
          <DropdownMenuLabel>
            {item.title} {item.badge ? `(${item.badge})` : ""}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.items.map((sub) => {
            // Render sub-item using SidebarMenuLink logic, which now handles params correctly
            return (
              <DropdownMenuItem asChild key={`${sub.title}-${sub.url}`}>
                {/* We need to reconstruct the Link here as SidebarMenuLink isn't directly usable */}
                <SidebarMenuLinkWrapper
                  href={href}
                  item={sub}
                  tenantId={tenantId}
                />
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};

function checkIsActive(
  currentHref: string,
  item: NavItem,
  tenantId?: string, // Accept tenantId
  checkSubItems = false // Flag to check sub-items for collapsible parent
): boolean {
  const currentPath = currentHref.split("?")[0]; // Ignore query params

  if ("items" in item) {
    if (!item.items?.length) {
      return false;
    }
    return !!item.items.some((subItem) =>
      checkIsActive(currentHref, subItem, tenantId, checkSubItems)
    );
  }

  const linkItem = item as NavLink;
  const expectedPath = resolveExpectedPath(linkItem.url, tenantId);

  return currentPath === expectedPath;
}

function resolveNavUrl(url: string, tenantId: string | undefined): string {
  if (!url.includes("$tenantId")) {
    return url;
  }

  if (!tenantId) {
    return url;
  }

  return url.replace("$tenantId", tenantId);
}

function resolveExpectedPath(
  url: string,
  tenantId: string | undefined
): string {
  if (!url.includes("$tenantId")) {
    return url;
  }

  return tenantId ? url.replace("$tenantId", tenantId) : TENANT_PLACEHOLDER;
}
